import { useEffect, useState } from 'react';
import { Box, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Save, X } from 'react-feather';
import { RichTextEditor } from '../../../../components/common/RichTextEditor/RichTextEditor';
import { participantsApi } from '../../../../services/api/participants';

interface ParticipantNotesTabProps {
  cohortId: string;
  participantId: string;
}

export function ParticipantNotesTab({ cohortId, participantId }: ParticipantNotesTabProps) {
  const [savedNotes, setSavedNotes] = useState('');
  const [currentNotes, setCurrentNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    participantsApi
      .getInternalNotes(cohortId, participantId)
      .then((result) => {
        if (cancelled) return;
        const initial = result.internal_notes ?? '';
        setSavedNotes(initial);
        setCurrentNotes(initial);
      })
      .catch((err) => {
        if (cancelled) return;
        notifications.show({
          title: 'Could not load notes',
          message: err instanceof Error ? err.message : 'Failed to load internal notes',
          color: 'red',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cohortId, participantId]);

  const hasChanges = currentNotes !== savedNotes;

  const handleCancel = () => {
    setCurrentNotes(savedNotes);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = currentNotes.trim() === '' ? null : currentNotes;
      const result = await participantsApi.updateInternalNotes(cohortId, participantId, payload);
      const next = result.internal_notes ?? '';
      setSavedNotes(next);
      setCurrentNotes(next);
      notifications.show({
        title: 'Notes saved',
        message: 'Internal notes have been updated',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Failed to save internal notes',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Internal Medley Notes
          </Text>
          <Text size="xs" c="dimmed" fs="italic">
            These notes are visible only to Medley admins and cohort managers.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<X size={12} />}
            onClick={handleCancel}
            disabled={!hasChanges || saving || loading}
          >
            Cancel
          </Button>
          <Button
            size="compact-xs"
            variant="light"
            color="medleyGreen"
            leftSection={<Save size={12} />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges || loading}
          >
            Save
          </Button>
        </Group>
      </Group>
      {loading ? (
        <Box ta="center" py="xl">
          <Loader size="sm" />
        </Box>
      ) : (
        <RichTextEditor
          value={currentNotes}
          onChange={setCurrentNotes}
          placeholder="Add internal notes about this participant..."
          minHeight={240}
          disabled={saving}
        />
      )}
    </Stack>
  );
}

export default ParticipantNotesTab;
